import { LinkClick, StructuredActionTaken } from "@swivl-mono/types";
import type { ChatPrimeRequest } from "@swivl-mono/types";
import { Socket, io } from "socket.io-client";

import { ChatMessage } from "../Types/BotChat.Types";

interface ServerToClientEvents {
  message: (payload: ChatMessage) => void;
  typing: (payload: { isTyping: boolean }) => void;
  history: (payload: { messages: ChatMessage[]; conversationId: number }) => void;
  identified: (payload: { userId: number; name?: string; chatToken?: string }) => void;
  error: (payload: { code: string; message: string }) => void;
  /** Structured-message protocol §6.2 — actionTaken was updated. */
  "message-updated": (payload: { messageId: string; actionTaken: unknown }) => void;
  /** Structured-message protocol §6.2 — server rejected an action-taken event. */
  "action-rejected": (payload: { messageId: string; reason: string }) => void;
  /** Widget design doc §A4.6 — server confirmed a `payload.data` update on a structured message. */
  "payload-updated": (payload: { messageId: string; data: Record<string, unknown> }) => void;
  /** ENG-3363 — a call-script step sent the customer to one of our pages; the widget navigates the window. */
  navigate: (payload: { conversationId: number; url: string; page: string; label: string }) => void;
  /**
   * Delivery/seen state changed for one or more messages (realtime-unification
   * Phase 3c) — published to the conv room. Advances the user's delivery
   * ticks: `seenBy.members` (an agent read the conversation) flips them to "seen".
   */
  "message-status": (payload: {
    conversationId: number;
    updates: { messageId: string; messageIndex: number; deliveryStatus?: string; seenBy?: ChatMessage["seenBy"] }[];
  }) => void;
}

interface ClientToServerEvents {
  message: (payload: { text: string; messageId: string; url?: string }) => void;
  ack: (payload: { messageId: string }) => void;
  read: () => void;
  history: (payload: { lastMessageIndex?: number }) => void;
  "new-conversation": () => void;
  /** Open primed after the handshake already happened (ENG-3166: a CTA clicked on an already-connected socket). */
  prime: (payload: { prime: ChatPrimeRequest }) => void;
  identify: (payload: { name?: string; email?: string; phone?: string }) => void;
  /** Rebuild user context (verification/focus/prompt) without reconnecting — ENG-3190. */
  "refresh-context": () => void;
  "page-navigation": (payload: { url: string }) => void;
  /**
   * Whether the user is actively viewing the chat (panel open + tab
   * focused). Lets the CC distinguish "chatting" from "connected but chat
   * closed". Emitted on panel open/close + on connect.
   */
  viewing: (payload: { viewing: boolean }) => void;
  /** Structured-message protocol §6.2 — user interacted with a message. */
  "action-taken": (payload: { messageId: string; action?: StructuredActionTaken; click?: LinkClick }) => void;
  /**
   * Widget design doc §A4.6 — widget pushed a new `payload.data` for a
   * structured message after a filter refresh / zip-entry submit / etc.
   */
  "picker-data-updated": (payload: { messageId: string; data: Record<string, unknown> }) => void;
  /**
   * Widget design doc §5 — change-location-from-unit-picker sub-flow. The
   * widget overlays a full-embed location-picker on top of an active
   * unit-picker; when the user picks a new location, this fires. Server
   * applies focus update + fetches units + updates the parent unit-picker
   * payload in place + emits `payload-updated`. LLM follow-up suppressed.
   */
  "subflow-location-changed": (payload: { unitPickerMessageId: string; newLocationId: number }) => void;
}

export type BotChatSocketType = Socket<ServerToClientEvents, ClientToServerEvents>;

export function createSocket(
  server: string,
  token: string,
  userId: number,
  orgId: number,
  initialUrl?: string,
  channel?: string,
  isTest?: boolean,
  /** The v4 widget's publicId — lets the bot apply the widget's display rules (ENG-2753). */
  publicId?: string,
  /** Open primed (ENG-3112): a starter slug from the URL, or a CTA prime (ENG-3166). One-shot — see useChat. */
  prime?: ChatPrimeRequest
): BotChatSocketType {
  return io(server, {
    auth: { token, userId, orgId, url: initialUrl, channel, isTest, publicId, prime },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000
  });
}
